import { useEffect, useState } from "react";
import {
  ArrowRight,
  BellPlus,
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  LoaderCircle,
  RotateCcw,
  Search,
  Scale,
} from "lucide-react";
import { expedienteDetailPath } from "@tribunalea/shared";
import {
  DataTable,
  DataTableAction,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
} from "../DataTable.jsx";
import { PageHeader } from "../PageHeader.jsx";
import { useRouter } from "../router.jsx";
import {
  buildExpedienteSearchUrl,
  expedientePaginationItems,
  expedienteSearchPageFromUrl,
  expedienteSearchQueryFromUrl,
  prepareExpedienteSearchQuery,
} from "./lib/expediente-search-query.js";

function resultKey(result) {
  return result.id || result.cuij || result.externalId || result.numero;
}

function resultIdentifier(result) {
  return result.cuij || result.externalId || result.numero || result.id;
}

function searchResultStatus(result) {
  if (result.isArchived && result.isFollowed) {
    return {
      label: "Archivado y seguido",
      description: "En Mis seguimientos",
      tone: "warning",
    };
  }
  if (result.isArchived) {
    return {
      label: "Archivado",
      description: "Puede reactivarse",
      tone: "warning",
    };
  }
  if (result.isSaved && result.isFollowed) {
    return {
      label: "Guardado y seguido",
      description: "En ambas secciones",
      tone: "success",
    };
  }
  if (result.isSaved) {
    return {
      label: "En Mis expedientes",
      description: "Guardado",
      tone: "success",
    };
  }
  if (result.isFollowed) {
    return {
      label: "En Mis seguimientos",
      description: "Con seguimiento",
      tone: "success",
    };
  }
  return {
    label: "Sin agregar",
    description: "Disponible",
    tone: "neutral",
  };
}

function SearchResultsTable({
  readOnly,
  results,
  pendingAction,
  onAddToExpedientes,
  onAddToFollowUps,
  onRestore,
  onOpen,
}) {
  return (
    <DataTable
      ariaLabel="Resultados de búsqueda de expedientes"
      className="expediente-search-table"
      density="compact"
      minWidth={readOnly ? "700px" : "880px"}
    >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeaderCell className="expediente-search-table-case">
            Expediente
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="expediente-search-table-court"
            priority="secondary"
          >
            Organismo
          </DataTableHeaderCell>
          {!readOnly && (
            <DataTableHeaderCell className="expediente-search-table-status">
              Estado
            </DataTableHeaderCell>
          )}
          <DataTableHeaderCell
            className="expediente-search-table-actions-cell"
            align="end"
          >
            Acciones
          </DataTableHeaderCell>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {results.map((result) => {
          const identifier = resultIdentifier(result);
          const managementKey = result.management?.caseKey;
          const supportsPraxisActions = !readOnly && Boolean(managementKey);
          const canOpenDetails = Boolean(result.detail?.identifier);
          const actionPending = Boolean(pendingAction);
          const savedAction = pendingAction === `${managementKey}:saved`;
          const followedAction = pendingAction === `${managementKey}:followed`;
          const restoreAction = pendingAction === `${managementKey}:restore`;
          return (
            <DataTableRow
              key={resultKey(result)}
              label={canOpenDetails
                ? `Abrir expediente ${identifier}`
                : undefined}
              onActivate={canOpenDetails
                ? () => onOpen(result)
                : undefined}
            >
              <DataTableCell className="expediente-search-table-case">
                <DataTablePrimaryCell
                  icon={<Scale size={16} aria-hidden="true" />}
                  eyebrow={identifier}
                  title={result.caratula || "Sin carátula informada"}
                  meta={result.display?.meta}
                />
              </DataTableCell>
              <DataTableCell priority="secondary">
                <DataTableValue
                  primary={result.organismoActual || "No informado"}
                  secondary={result.fuero || "Fuero no informado"}
                />
              </DataTableCell>
              {!readOnly && (
                <DataTableCell>
                  <DataTableStatus {...(
                    result.display?.status || searchResultStatus(result)
                  )} />
                </DataTableCell>
              )}
              <DataTableCell align="end">
                <div className="expediente-search-table-actions">
                  {supportsPraxisActions &&
                    !result.isSaved && !result.isArchived && (
                    <DataTableAction
                      label={`Agregar ${identifier} a Mis expedientes`}
                      disabled={actionPending}
                      onClick={() => onAddToExpedientes(result)}
                      icon={savedAction ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <FilePlus2 size={15} />
                      )}
                    >
                      {savedAction ? "Agregando…" : "Expedientes"}
                    </DataTableAction>
                  )}
                  {supportsPraxisActions && result.isArchived && (
                    <DataTableAction
                      label={`Reactivar expediente ${identifier}`}
                      disabled={actionPending}
                      onClick={() => onRestore(result)}
                      icon={restoreAction ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <RotateCcw size={15} />
                      )}
                    >
                      {restoreAction ? "Reactivando…" : "Reactivar"}
                    </DataTableAction>
                  )}
                  {supportsPraxisActions && !result.isFollowed && (
                    <DataTableAction
                      label={`Agregar ${identifier} a Mis seguimientos`}
                      disabled={actionPending}
                      onClick={() => onAddToFollowUps(result)}
                      icon={followedAction ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <BellPlus size={15} />
                      )}
                    >
                      {followedAction ? "Agregando…" : "Seguimiento"}
                    </DataTableAction>
                  )}
                  {!supportsPraxisActions && !readOnly && (
                    <span className="expediente-search-external-label">
                      {result.display?.passiveActionLabel || "Sólo consulta"}
                    </span>
                  )}
                  {canOpenDetails && (
                    <DataTableAction
                      label={`Ver detalles de ${identifier}`}
                      disabled={actionPending}
                      onClick={() => onOpen(result)}
                      icon={<ArrowRight size={15} />}
                    >
                      {readOnly ? "Consultar" : undefined}
                    </DataTableAction>
                  )}
                </div>
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

export function ExpedienteSearchPage({
  api,
  detailBasePath = "/expedientes",
  hero = false,
  productName = "Praxis",
  readOnly = false,
  searchPath = "/buscar-expedientes",
}) {
  const { search: routeSearch, navigate } = useRouter();
  const routeQuery = expedienteSearchQueryFromUrl(routeSearch);
  const routePage = expedienteSearchPageFromUrl(routeSearch);
  const [query, setQuery] = useState(routeQuery);
  const [reloadToken, setReloadToken] = useState(0);
  const [lookingUp, setLookingUp] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [sources, setSources] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const prepared = prepareExpedienteSearchQuery(routeQuery);
    setQuery(routeQuery);
    setError("");
    setSources([]);

    if (!routeQuery) {
      setLookingUp(false);
      return () => {
        active = false;
      };
    }
    if (prepared.kind === "invalid") {
      setLookingUp(false);
      setError(
        "Ingresá un número de expediente o al menos 3 caracteres de la carátula.",
      );
      return () => {
        active = false;
      };
    }

    setLookingUp(true);
    api.lookupJudiciaryExpedientes({
      query: prepared.query,
      page: routePage,
    })
      .then((result) => {
        if (!active) return;
        setSources(result.sources || []);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLookingUp(false);
      });

    return () => {
      active = false;
    };
  }, [api, reloadToken, routePage, routeQuery]);

  const updateResult = (caseKey, patch) => {
    setSources((current) => current.map((source) => ({
      ...source,
      items: source.items.map((result) =>
        result.management?.caseKey === caseKey
          ? { ...result, ...patch }
          : result,
      ),
    })));
  };

  const lookupExpediente = async (event) => {
    event.preventDefault();
    const prepared = prepareExpedienteSearchQuery(query);
    if (prepared.kind === "invalid") {
      setError(
        "Ingresá un número de expediente o al menos 3 caracteres de la carátula.",
      );
      return;
    }

    if (prepared.query === routeQuery) {
      setReloadToken((current) => current + 1);
    } else {
      navigate(buildExpedienteSearchUrl(prepared.query, { searchPath }));
    }
  };

  const runResultAction = async (result, action, request, patch) => {
    if (pendingAction) return;
    const caseKey = result.management?.caseKey;
    if (!caseKey) return;
    setPendingAction(`${caseKey}:${action}`);
    setError("");
    try {
      const response = await request();
      updateResult(caseKey, { ...response, ...patch });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setPendingAction("");
    }
  };

  const resultProps = {
    pendingAction,
    onAddToExpedientes: (result) =>
      runResultAction(
        result,
        "saved",
        () => api.addExpediente(result.management.caseKey),
        { isSaved: true, isArchived: false },
      ),
    onAddToFollowUps: (result) =>
      runResultAction(
        result,
        "followed",
        async () => (await api.addFollowUps([
          result.management.caseKey,
        ]))[0],
        { isFollowed: true },
      ),
    onRestore: (result) =>
      runResultAction(
        result,
        "restore",
        () => api.restoreExpediente(result.management.caseKey),
        { isSaved: true, isArchived: false },
      ),
    onOpen: (result) =>
      navigate(expedienteDetailPath(
        result.detail.identifier,
        "informacion",
      ).replace(/^\/expedientes/u, detailBasePath)),
  };
  const goToPage = (pagination, page) => {
    if (
      page === pagination?.page ||
      page < 1 ||
      page > pagination?.totalPages
    ) {
      return;
    }
    navigate(buildExpedienteSearchUrl(routeQuery, { page, searchPath }));
  };

  return (
    <div className="page expediente-search-page">
      <PageHeader
        className="expedientes-heading"
        hero={hero}
        eyebrow={productName}
        title="Buscar expedientes"
        description="Consultá por número de expediente o carátula en las fuentes judiciales conectadas."
      />

      <section className="expediente-lookup-card">
        <div className="expediente-lookup-copy">
          <span className="expediente-lookup-icon">
            <Search size={21} />
          </span>
          <strong>Consultar expediente</strong>
        </div>
        <form onSubmit={lookupExpediente} className="expediente-lookup-form">
          <label>
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setError("");
                setSources([]);
              }}
              placeholder="Número de expediente o texto de la carátula"
              aria-label="Número de expediente o texto de la carátula"
              autoComplete="off"
              disabled={lookingUp || Boolean(pendingAction)}
              aria-invalid={Boolean(error)}
            />
          </label>
          <button
            className="button primary"
            type="submit"
            disabled={lookingUp || Boolean(pendingAction)}
          >
            {lookingUp ? (
              <LoaderCircle className="spin" size={18} />
            ) : (
              <Search size={18} />
            )}
            {lookingUp ? "Consultando…" : "Consultar"}
          </button>
        </form>

        {error && (
          <p className="field-error expediente-lookup-error">{error}</p>
        )}

        {sources.length > 0 && (
          <div className="expediente-lookup-results">
            {sources.map((source) => {
              const total = source.pagination?.totalItems
                ?? source.items.length;
              const paginationItems = expedientePaginationItems(
                source.pagination?.page || 1,
                source.pagination?.totalPages || 1,
              );
              return (
                <section className="expediente-search-source" key={source.id}>
                  <div className="expediente-lookup-results-heading">
                    <strong>{source.name}</strong>
                    {source.status === "ready" && (
                      <span>
                        {total} {total === 1 ? "expediente" : "expedientes"}
                      </span>
                    )}
                  </div>
                  {source.status !== "ready" ? (
                    <div className={`expediente-search-source-message is-${source.status}`}>
                      <span>{source.message}</span>
                      {source.action && (
                        <button
                          className="button secondary"
                          type="button"
                          onClick={() => navigate(source.action.href)}
                        >
                          {source.action.label}
                        </button>
                      )}
                    </div>
                  ) : source.items.length ? (
                    <SearchResultsTable
                      readOnly={readOnly}
                      results={source.items}
                      {...resultProps}
                    />
                  ) : (
                    <p className="expediente-search-source-empty">
                      No encontramos expedientes que coincidan en esta fuente.
                    </p>
                  )}
                  {source.status === "ready" &&
                    source.pagination?.totalPages > 1 && (
                    <nav
                      className="expediente-search-pagination"
                      aria-label={`Páginas de resultados de ${source.name}`}
                    >
                      <button
                        type="button"
                        aria-label="Página anterior"
                        disabled={source.pagination.page <= 1 || lookingUp}
                        onClick={() => goToPage(
                          source.pagination,
                          source.pagination.page - 1,
                        )}
                      >
                        <ChevronLeft size={17} />
                      </button>
                      {paginationItems.map((item) =>
                        typeof item === "number" ? (
                          <button
                            className={
                              item === source.pagination.page ? "active" : ""
                            }
                            type="button"
                            key={item}
                            aria-label={`Página ${item}`}
                            aria-current={
                              item === source.pagination.page
                                ? "page"
                                : undefined
                            }
                            disabled={lookingUp}
                            onClick={() => goToPage(
                              source.pagination,
                              item,
                            )}
                          >
                            {item}
                          </button>
                        ) : (
                          <span key={item} aria-hidden="true">…</span>
                        ),
                      )}
                      <button
                        type="button"
                        aria-label="Página siguiente"
                        disabled={
                          source.pagination.page >=
                            source.pagination.totalPages || lookingUp
                        }
                        onClick={() => goToPage(
                          source.pagination,
                          source.pagination.page + 1,
                        )}
                      >
                        <ChevronRight size={17} />
                      </button>
                    </nav>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
